import {
  getDataDictionaries,
  getSpecificDataDictionary,
  activateDataDictionary,
  getLatestS3Version,
  getLatestDataDictionary,
  getDiffsFromCurrentDataDictionary,
} from './admin-service';
import { modalUtils } from '@/app/core/utils';
import { Table } from 'antd';
import {
  Button,
  Tooltip,
  Icon,
  Text,
  Divider,
  Flex,
  Grid,
  GridItem,
  Link,
  VStack,
} from '@chakra-ui/react';
import { Popover, Modal, Tabs, Spin } from '@ssar-app/uikit';
import { useEffect, useState } from 'react';
import dayjs, { extend as dayJsExtend } from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import {
  DataDictionaryDiffTypeEnum,
  DataDictionaryPlaceholder,
  fetchAndEnforceApiResponseData,
  AscendQuestDataDictionarySchema,
  DataDictionaryDiff,
  S3DataDictionay,
} from '@ssar-app/domain';
import ReactJson from 'react-json-view';
import { saveAs } from 'file-saver';
import {
  AiOutlineCheck,
  AiOutlineCheckCircle,
  AiOutlineDiff,
  AiOutlineDownload,
  AiOutlineExclamationCircle,
  AiOutlineInfoCircle,
  AiOutlineStop,
} from 'react-icons/ai/index.js';
import { FaRedo } from 'react-icons/fa/index.js';

dayJsExtend(utcPlugin);

export const SelectDataDictionary = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<S3DataDictionay[]>([]);
  const [showPreviewModel, setPreviewModel] = useState(false);
  const [previewData, setPreviewData] =
    useState<AscendQuestDataDictionarySchema>();
  const [previewHeader, setPreviewHeader] = useState<string>('');
  const [sortedDiff, setSortedDiff] = useState<DataDictionaryDiff[][]>();
  const [showDiffModel, setDiffModel] = useState<boolean>(false);
  const [deltaDiffCount, setDeltaDiffCount] = useState<number>(0);

  interface ButtonProps {
    row: S3DataDictionay;
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'Key',
      render: (text: string, row: S3DataDictionay) => (
        <Flex alignItems='center'>
          <PreviewButton row={row}></PreviewButton>
          &nbsp;|&nbsp;
          <DownloadButton row={row}></DownloadButton>
          &nbsp;|&nbsp;
          <DiffButton row={row}></DiffButton>
          &nbsp;{text}&nbsp;
          {row.activateDetails && (
            <Tooltip label='Currently active'>
              <Icon as={AiOutlineCheckCircle} color='green.500' fontSize='md' />
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'Size',
      render: (size: number) => {
        if (size === 0) {
          return '0 Bytes';
        }
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(size) / Math.log(1024));
        return (size / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
      },
      sorter: (a: S3DataDictionay, b: S3DataDictionay) =>
        (b.Size || 0) - (a.Size || 0),
    },
    {
      title: 'Last Modified Date',
      dataIndex: 'LastModified',
      render: (text: Date) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
      sorter: (a: S3DataDictionay, b: S3DataDictionay) =>
        dayjs(b.LastModified).diff(dayjs(a.LastModified)),
    },
    {
      title: 'Activate',
      key: 'active',
      render: (text: string, row: S3DataDictionay) => (
        <ActivateButton row={row}></ActivateButton>
      ),
    },
  ];

  useEffect(() => {
    getData();
  }, []);

  const getData = (): void => {
    setIsLoading(true);
    Promise.all([
      fetchAndEnforceApiResponseData(getDataDictionaries),
      fetchAndEnforceApiResponseData(getLatestS3Version),
    ])
      .then(([datadictionaries, latestS3Version]) => {
        setData(
          datadictionaries
            ?.filter(
              (item) => item.Key != DataDictionaryPlaceholder.VersionFileName,
            )
            ?.map((a: S3DataDictionay) => {
              if (
                a.Key === latestS3Version?.key &&
                a.VersionId === latestS3Version?.version
              ) {
                a.activateDetails = latestS3Version;
              }
              return a;
            }) || [],
        );
      })
      .catch((e) => {
        modalUtils.showError(e, 'Error while fetching data dictionaries');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const ActivateButton = (props: ButtonProps) => {
    const activateClicked = (): void => {
      setIsLoading(true);
      fetchAndEnforceApiResponseData(() =>
        activateDataDictionary(props.row.Key || '', props.row.VersionId || ''),
      )
        .then((data) => {
          // console.log(data);
          if (data) {
            modalUtils.showSuccessMessage('Selected data dictionary activated');
          } else {
            modalUtils.showError('Error while performing Activate action');
          }
          getData();
        })
        .catch((e) => {
          modalUtils.showError(e, 'Error while performing Activate action');
          setIsLoading(false);
        });
    };
    return (
      <Popover
        placement='top'
        message={
          <>
            <Icon
              as={AiOutlineExclamationCircle}
              color='orange.500'
              verticalAlign='middle'
              mr={1}
            />
            Are you sure you want to activate this data dictionary?
          </>
        }
        onOk={() => activateClicked()}
        okText='Yes'
        cancelText='No'
        okProps={{ colorScheme: 'blue', variant: 'solid' }}
        triggerButtonProps={{ color: 'gray.800' }}
      >
        <Tooltip
          label={
            props.row.activateDetails
              ? 'Currently active'
              : 'Make this data dictionary active'
          }
          placement='left'
        >
          <Button
            disabled={!!props.row.activateDetails}
            leftIcon={<Icon as={AiOutlineCheck} />}
            variant='outline'
            colorScheme={'gray'}
          >
            Make Active
          </Button>
        </Tooltip>
      </Popover>
    );
  };

  const previewOrDownload = (
    key?: string,
    version?: string,
    isDownload?: boolean,
  ): void => {
    setIsLoading(true);
    fetchAndEnforceApiResponseData(() =>
      key && version
        ? getSpecificDataDictionary(key, version)
        : getLatestDataDictionary(),
    )
      .then((data) => {
        if (isDownload) {
          const jsonBlob = new Blob([JSON.stringify(data, null, 2)], {
            type: 'application/json',
          });
          saveAs(jsonBlob, key || 'ascend-quest-data-dictionary.json');
        } else {
          setPreviewHeader(key || '');
          setPreviewData(data);
          setPreviewModel(true);
        }
      })
      .catch((e) => {
        modalUtils.showError(e, 'Error while previewing data dictionary');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  type DeltaProps = {
    value: Record<string, unknown>[];
    valueType: string;
    type: string;
    bgColor: string;
  };

  const DeltaDiff = (props: DeltaProps) => {
    return (
      <Flex
        direction={'column'}
        backgroundColor={props.bgColor}
        width={
          props.type === DataDictionaryDiffTypeEnum.UPDATE ? '50%' : '100%'
        }
      >
        {props.valueType === 'Object' || props.valueType === 'Array' ? (
          <ReactJson
            src={props.value || {}}
            name={null}
            enableClipboard={false}
            collapsed={2}
            displayDataTypes={false}
            displayObjectSize={false}
          />
        ) : (
          <Text>{props.value}</Text>
        )}
      </Flex>
    );
  };

  const showDifference = (version: string): void => {
    setIsLoading(true);
    setDiffModel(true);
    fetchAndEnforceApiResponseData(() =>
      getDiffsFromCurrentDataDictionary(version),
    )
      .then((deltaDiff) => {
        setDeltaDiffCount(deltaDiff?.length || 0);
        setSortedDiff(
          Object.values(DataDictionaryDiffTypeEnum).map(
            (type) => deltaDiff?.filter((a) => a.type === type) || [],
          ),
        );
      })
      .catch((e) => {
        modalUtils.showError(e, 'Error while fetching data dictionaries');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const PreviewButton = (props: ButtonProps) => {
    return (
      <Tooltip label='Preview this data dictionary' placement='top'>
        <Link
          display='flex'
          href='/preview-data-dictionary'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            previewOrDownload(props.row.Key, props.row.VersionId);
          }}
        >
          <Icon as={AiOutlineInfoCircle} fontSize='md' />
        </Link>
      </Tooltip>
    );
  };

  const DownloadButton = (props: ButtonProps) => {
    return (
      <Tooltip label='Download this data dictionary' placement='top'>
        <Link
          display='flex'
          href='/download-data-dictionary'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            previewOrDownload(props.row.Key, props.row.VersionId, true);
          }}
        >
          <Icon as={AiOutlineDownload} fontSize='md' />
        </Link>
      </Tooltip>
    );
  };

  const DiffButton = (props: ButtonProps) => {
    return props.row.activateDetails ? (
      <Icon as={AiOutlineStop} color='#eb2f96' />
    ) : (
      <Tooltip label='Difference from current data dictionary' placement='top'>
        <Link
          display='flex'
          href='/difference-data-dictionary'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            showDifference(props.row.VersionId || '');
          }}
        >
          <Icon as={AiOutlineDiff} fontSize='md' />
        </Link>
      </Tooltip>
    );
  };

  return (
    <>
      <Modal
        isOpen={showDiffModel}
        headerProps={isLoading ? { display: 'none' } : { display: 'block' }}
        header={`Difference from current data dictionary (Total Changes: ${deltaDiffCount})`}
        size={isLoading ? 'md' : '6xl'}
        onClose={() => setDiffModel(false)}
        scrollBehavior={isLoading ? 'outside' : 'inside'}
        showCloseButton={isLoading ? false : true}
        closeOnOverlayClick={false}
        showDivider={isLoading ? false : true}
        showOk={!isLoading}
        onOk={() => setDiffModel(false)}
        bodyProps={isLoading ? {} : { pt: 4, pb: 4 }}
      >
        {isLoading ? (
          <VStack>
            <Spin tip='' spinning={isLoading}></Spin>
            <Text textAlign='center' fontSize='md' pt={24}>
              Loading difference from current data dictionary...
            </Text>
          </VStack>
        ) : (
          <Tabs
            variant='line'
            items={Object.values(DataDictionaryDiffTypeEnum).map((type, i) => ({
              label: `${type} (${sortedDiff && sortedDiff[i].length})`,
              key: type,
              content:
                sortedDiff &&
                sortedDiff[i].length > 0 &&
                sortedDiff[i].map((diff, index) => (
                  <div key={`json-diff-${index}`}>
                    <Text>
                      Change Path:{' '}
                      <Text color={'rgba(0,0,0,.45)'} display='inline'>
                        {diff.path}
                      </Text>
                    </Text>
                    <Flex justify='space-between'>
                      {type !== DataDictionaryDiffTypeEnum.ADD && (
                        <DeltaDiff
                          value={diff.oldValue}
                          valueType={diff.valueType}
                          type={type}
                          bgColor='#ffeef0'
                        ></DeltaDiff>
                      )}
                      {type !== DataDictionaryDiffTypeEnum.REMOVE && (
                        <DeltaDiff
                          value={diff.newValue}
                          valueType={diff.valueType}
                          type={type}
                          bgColor='#e6ffed'
                        ></DeltaDiff>
                      )}
                    </Flex>
                    <Divider
                      borderColor='gray.300'
                      borderWidth='thin'
                      marginY='6'
                      hidden={index === sortedDiff[i].length - 1}
                    />
                  </div>
                )),
            }))}
            tabListProps={{ borderColor: 'gray.500' }}
          />
        )}
      </Modal>
      <Grid templateColumns='repeat(2, 1fr)'>
        <GridItem>
          <Text fontSize={'lg'} color={'black'} fontWeight='700'>
            Data Dictionaries
          </Text>
        </GridItem>
        <GridItem>
          <Flex gap={2} pb={3} justify='end'>
            <Button
              onClick={() => getData()}
              leftIcon={<Icon as={FaRedo} fontSize='md' />}
              colorScheme={'gray'}
              variant='outline'
            >
              Refresh
            </Button>
            <Tooltip label='Preview currently active data dictionary'>
              <Button
                onClick={() => previewOrDownload()}
                leftIcon={<Icon as={AiOutlineInfoCircle} fontSize='md' />}
                colorScheme={'gray'}
                variant='outline'
              >
                Preview current data dictionary
              </Button>
            </Tooltip>
            <Tooltip label='Download currently active data dictionary'>
              <Button
                onClick={() => previewOrDownload(undefined, undefined, true)}
                leftIcon={<Icon as={AiOutlineDownload} fontSize='md' />}
                colorScheme={'gray'}
                variant='outline'
              >
                Download current data dictionary
              </Button>
            </Tooltip>
          </Flex>
        </GridItem>
      </Grid>
      <Table
        className='table-data-list'
        size='small'
        loading={isLoading && !showDiffModel}
        columns={columns}
        dataSource={data}
        pagination={{
          total: data.length,
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomLeft'],
          showSizeChanger: true,
          simple: false,
        }}
        expandable={{
          expandedRowRender: (record) => (
            <Text ml={12} mb={0}>{`Activated by ${
              record.activateDetails?.createdBy
            } on ${dayjs(record.activateDetails?.createdDate).format(
              'YYYY-MMM-DD HH:mm:ss [GMT]ZZ',
            )}`}</Text>
          ),
          rowExpandable: (record) => !!record.activateDetails,
        }}
      />
      <Modal
        header={`Preview of ${
          previewHeader && previewHeader != ''
            ? previewHeader
            : 'current data dictionary'
        }`}
        isOpen={showPreviewModel}
        onClose={() => setPreviewModel(false)}
        size='6xl'
        scrollBehavior={'inside'}
        showOk
      >
        <ReactJson
          src={previewData || {}}
          name={null}
          enableClipboard={false}
          collapsed={1}
          displayDataTypes={false}
          displayObjectSize={false}
        />
      </Modal>
    </>
  );
};
